import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/admin/properties/$propertyId')({
  component: AdminPropertyDetail,
});

type AdminProperty = {
  id: string;
  name: string;
  defaultMowCadenceDays: number | null;
  archivedAt: string | null;
  archivedReason: string | null;
  geometryVerifiedAt: string | null;
  owner: { id: string; name: string; email: string } | null;
};

const TRANSFER_ERRORS: Record<string, string> = {
  target_not_member: 'That user is not a current member of this property.',
  already_owner: 'That user is already the owner.',
  no_current_owner: 'This property has no current owner.',
  validation_failed: 'User ID is required.',
};

function AdminPropertyDetail() {
  const { propertyId } = Route.useParams();
  const qc = useQueryClient();
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferUserId, setTransferUserId] = useState('');
  const [transferError, setTransferError] = useState<string | null>(null);

  const { data, isPending, error } = useQuery({
    queryKey: ['admin-property', propertyId],
    queryFn: () =>
      api
        .get<{ property: AdminProperty }>(`/v1/admin/properties/${propertyId}`)
        .then((r) => r.property),
  });

  const transferMutation = useMutation({
    mutationFn: () =>
      api
        .post<{ property: AdminProperty }>(
          `/v1/admin/properties/${propertyId}/transfer-ownership`,
          { toUserId: transferUserId.trim() },
        )
        .then((r) => r.property),
    onSuccess: (updated) => {
      qc.setQueryData(['admin-property', propertyId], updated);
      void qc.invalidateQueries({ queryKey: ['admin-properties'] });
      setTransferOpen(false);
      setTransferUserId('');
      setTransferError(null);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setTransferError(
          TRANSFER_ERRORS[err.code ?? ''] ?? err.message ?? 'Transfer failed.',
        );
      } else {
        setTransferError('Transfer failed.');
      }
    },
  });

  if (isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (error || !data) return <p className="text-sm text-destructive">Failed to load property.</p>;

  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
        <Link to="/admin/properties" className="hover:text-foreground">
          Properties
        </Link>
        <span>/</span>
        <span className="text-foreground">{data.name}</span>
      </div>

      <div className="rounded-lg border border-border p-5 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div className="font-semibold text-lg">{data.name}</div>
          {data.archivedAt ? (
            <span className="text-xs text-muted-foreground shrink-0">
              Archived {new Date(data.archivedAt).toLocaleDateString()}
            </span>
          ) : (
            <span className="text-xs text-green-600 shrink-0">Active</span>
          )}
        </div>
        <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-muted-foreground">Owner</dt>
            <dd className="font-medium mt-0.5">
              {data.owner ? (
                <Link
                  to="/admin/users/$userId"
                  params={{ userId: data.owner.id }}
                  className="hover:underline"
                >
                  {data.owner.email}
                </Link>
              ) : (
                '—'
              )}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Geometry verified</dt>
            <dd className="mt-0.5">
              {data.geometryVerifiedAt
                ? new Date(data.geometryVerifiedAt).toLocaleDateString()
                : 'No'}
            </dd>
          </div>
          {data.defaultMowCadenceDays !== null && (
            <div>
              <dt className="text-muted-foreground">Default cadence</dt>
              <dd className="mt-0.5">{data.defaultMowCadenceDays} days</dd>
            </div>
          )}
        </dl>
      </div>

      <h2 className="font-medium mb-3">Transfer ownership</h2>
      {!transferOpen ? (
        <Button variant="outline" size="sm" onClick={() => setTransferOpen(true)}>
          Transfer ownership…
        </Button>
      ) : (
        <div className="rounded-lg border border-border p-4 flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">
            Enter the user ID of the new owner. They must already be a member of this property.
          </p>
          <input
            type="text"
            placeholder="User ID (UUID)"
            value={transferUserId}
            onChange={(e) => { setTransferUserId(e.target.value); setTransferError(null); }}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {transferError && <p className="text-sm text-destructive">{transferError}</p>}
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => { setTransferError(null); transferMutation.mutate(); }}
              disabled={!transferUserId.trim() || transferMutation.isPending}
            >
              {transferMutation.isPending ? 'Transferring…' : 'Transfer'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setTransferOpen(false);
                setTransferUserId('');
                setTransferError(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
